import { EventEmitter, Injectable, OnDestroy } from '@angular/core'
import {
  RootState,
  selectActiveSession,
  selectFocusedUIComponent,
  Session,
} from '@helios/state-management'
import { Store } from '@ngrx/store'
import {
  debounceTime,
  distinctUntilChanged,
  takeUntil,
  tap,
} from 'rxjs/operators'
import { TelemetryService } from './telemetry.service'

/*************FOCUS TRACKING***********************
 * OVERVIEW
 * ---------
 * Lots of cool stuff we can do with this data - we expect to use it to help with the usage report https://confluence.arbfund.com/display/PLATFORM/Helios+Usage+Report+Design
 * As well as analysis of how users navigate the app: https://help.fullstory.com/hc/en-us/articles/4410019422743-Journeys
 *
 * DETAILS
 * ---------
 * We send FocusChange events to FS/NR which include the time spent on a panel (or other major UI component)
 *   e.g. heliosUsageEvent: {componentName: "Account List", durationSec: 130}
 * This makes it easy to aggregate time spent, because the event row includes the other attributes we've set
 *   |userId|durationSec|componentName|accountId|sessionId|
 *
 * There are two tricksy parts to this:
 *   - Recording DURATION spent in a panel
 *   - Detecting IDLE time
 *
 * Recording DURATION
 * -----------
 * simply keep track of prior focus event and when we get a new focus event we attribute that time to the prior focused component
 *
 * Detecting IDLE
 * -----------
 * A user may be working, then take an hour meeting, then come back and pick up where they left off.
 * On the other hand, a user may spend a whole hour digging into details in the Distributions panel.
 * So how do we tell the difference?
 * We simply monitor for activity (clicks, scrolls, etc), and if one hasn't happened in 5 minutes, we set the current UI focus to UNDEFINED
 * This causes a time report event for the current panel
 *
 *
 ***************************************************/

type FocusEventData = {
  componentName: string
  startTime: Date
}

@Injectable({
  providedIn: 'root',
})
export class UsageReportService implements OnDestroy {
  readonly IDLE_TIME_LIMIT = 5 * 60 * 1000 //5 minutes
  //   readonly IDLE_TIME_LIMIT = 10 * 1000 // 10 sec - manual debug helper

  private readonly _destroyed$ = new EventEmitter<void>()
  /* @internal for test*/
  readonly _userActivity$ = new EventEmitter<void>()

  private _currentUIFocus?: FocusEventData
  private _priorComponentName?: string
  private _currentSession?: Session

  constructor(
    private readonly store: Store<RootState>,
    private readonly telemetry: TelemetryService,
  ) {
    this.store = store
    this.telemetry = telemetry
  }

  init() {
    this.store
      .select(selectFocusedUIComponent)
      .pipe(takeUntil(this._destroyed$))
      .subscribe((focusedPage: string | undefined) => {
        this.setFocusedUIComponent(focusedPage)
      })

    this.store
      .select(selectActiveSession)
      .pipe(
        takeUntil(this._destroyed$),
        distinctUntilChanged((a, b) => a?.id === b?.id),
      )
      .subscribe(session => {
        this.handleSessionChange(session)
      })

    this.initIdleMonitor()
  }

  ngOnDestroy() {
    this._destroyed$.next()
    this._destroyed$.complete()
  }

  /**
   * @internal for testing
   */
  setFocusedUIComponent(componentName: string | undefined) {
    const now = new Date()

    //SEND EVENT heliosFocusUI - focus events helps track usage patterns across panels
    //don't double-tap focus on same element - but we do want to emit focus on undefined, that's helpful to track idleness!
    if (componentName != this._currentUIFocus?.componentName) {
      this.telemetry.emitCustomEvent('heliosFocusUI', {
        componentName,
      })
      //   console.log(`FOCUS ${componentName}`) //manual debug helper
    }

    //keep track of prior focus so we can resume after idle (don't set to undefined when idle)
    if (componentName) this._priorComponentName = componentName

    //focus has changed! - record time spent on current component
    if (this._currentUIFocus) {
      const durationSec =
        (now.getTime() - this._currentUIFocus.startTime.getTime()) / 1000

      //SEND EVENT heliosUsage
      //note that telemetry will have already recorded userId, accountId, etc on the attributes, so those data points will be picked up by our query
      //when we support multiple accounts, we could emit one of these events for each account, that makes it easy to aggregate
      this.telemetry.emitCustomEvent('heliosUsageEvent', {
        componentName: this._currentUIFocus.componentName,
        durationSec,
        accountId: this.getLoadedAccountId(this._currentSession), //once we regularly support multiple accounts, we can phase out the single account report
        accountIds: this.getLoadedAccountIdsCsv(this._currentSession),
      })

      //   console.log(
      //     `USAGE ${this._currentUIFocus.componentName}, time(sec) ${durationSec}`,
      //   ) //manual debug helper
    }

    if (componentName)
      this._currentUIFocus = {
        componentName,
        startTime: now,
      }
    else this._currentUIFocus = undefined
  }

  initIdleMonitor() {
    //listen for focus events on panels and major UI components

    //monitor for any user activity in the page - this is how we detect idle.  Click and keyup are a nice balance without too many events like mouse movement
    //note: scroll event is a good choice, but it's suppressed by in zone-flags.ts for table scrolling performance
    const rootEl = document.getElementById('heliosBody')
    rootEl?.addEventListener('click', () => {
      this._userActivity$.next()
    })
    rootEl?.addEventListener('keyup', () => {
      this._userActivity$.next()
    })

    this._userActivity$
      .pipe(
        tap(() => {
          //start up again if we've been idle
          if (!this._currentUIFocus)
            this.setFocusedUIComponent(this._priorComponentName)
        }),
        debounceTime(this.IDLE_TIME_LIMIT),
      )
      .subscribe(() => {
        this.setFocusedUIComponent(undefined)
      })
  }

  /**
   * sends heliosStartSession / heliosEndSession events, sets sessionId and loaded accountId as custom attributes
   *
   * @param session
   */
  private handleSessionChange(session: Session | undefined) {
    if (session) {
      this._currentSession = session
      this.telemetry.emitCustomEvent('heliosStartSession', {
        heliosAccountId: this.getLoadedAccountId(this._currentSession),
        heliosSessionId: session.id,
      })
      this.telemetry.setCustomAttributes({
        heliosAccountId: this.getLoadedAccountId(this._currentSession),
        heliosSessionId: session.id,
      })
    } else {
      this.telemetry.emitCustomEvent('heliosEndSession', {
        heliosAccountId: this.getLoadedAccountId(this._currentSession),
        heliosSessionId: this._currentSession?.id,
      })
      this.telemetry.setCustomAttributes({
        heliosAccountId: undefined,
        heliosSessionId: undefined,
      })
      this._currentSession = undefined
    }
  }

  /**
   * @internal for test
   * @returns first loaded account if more than one, or undefined if none loaded or no session
   */
  getLoadedAccountId(session: Session | undefined) {
    if (!session) return undefined
    if (session.loadedAccountIds.length == 0) return undefined
    return session.loadedAccountIds[0]
  }

  /**
   * @internal for test
   * @returns csv list of all loaded account ids, or undefined if none loaded or no session
   */
  getLoadedAccountIdsCsv(session: Session | undefined) {
    if (!session) return undefined
    if (session.loadedAccountIds.length == 0) return undefined
    return session.loadedAccountIds.join(`,`)
  }
}
